// libs/components/src/lib/components/Sidebar/ToggleMenuButton/ToggleMenuButton.tsx

import React from "react";
import SidebarItem from "../SidebarItem/SidebarItem";
import { Button, IconButton } from "@kaltura/ds-react-components";
import { ChevronLeft24Icon, Clear24Icon } from "@kaltura/ds-react-icons";
import { translate } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { SidebarMenuItem } from "@kaltura/mediaspace-shared-types";

export type MenuProps = {
    title: string;
    label?: string;
    currentMenu: SidebarMenuItem[];
    focusedItem: number;
    openSidebar: boolean;
    closeButtonRef?: React.RefObject<HTMLButtonElement | null>;
    handleClickBackButton: () => void;
    handleClickSubMenuIcon: (menuItem: SidebarMenuItem) => void;
    handleKeyDownSubMenuIcon: (e: React.KeyboardEvent<HTMLButtonElement>, menuItem: SidebarMenuItem) => void;
    handleKeyDownBackButton: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
    handleKeyDownMenuItem: (e: React.KeyboardEvent<HTMLElement>, index: number, menuItem: SidebarMenuItem) => void;
    handleCloseSidebar?: () => void;
    handleKeyDownCloseButton?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
};

const CloseButton = styled(IconButton)(({ theme }) => {
    return {
        top: theme.spacing(1),
        left: 270,
        position: "absolute",
        zIndex: 1,
    };
});

const BackButton = styled(Button)(({ theme }) => {
    return {
        padding: theme.spacing(3),
        borderRadius: 0,
        width: 320,
        [`&:hover`]: {
            backgroundColor: theme.kaltura.palette.tone7,
        },

        [`& .kms-ds-sidebar-menu-back-btn-label`]: {
            fontSize: theme.typography.htmlFontSize,
            fontWeight: theme.kaltura.typography.fontWeightBold,
            color: theme.kaltura.palette.tone1,
        },
    };
});

const StyledUlContainer = styled("div")({
    position: "relative",
});

const StyledUl = styled(
    "ul",
    {
        shouldForwardProp: (propName) => propName !== "hasBackButton",
    }
)<{ hasBackButton?: boolean }>(({ theme, hasBackButton }) => {
    return {
        margin: 0,
        height: "100vh",
        width: 320,
        overflowY: "auto",
        overflowX: "hidden",
        backgroundColor: theme.kaltura.palette.tone8,
        padding: 0,
        paddingTop: hasBackButton ? 0 : theme.spacing(6),
    };
});
/*
 * Sidebar Menu component - single visible menu inside the sidebar.
 */
const Menu = React.forwardRef<HTMLUListElement, MenuProps>(
    (
        {
            title = "",
            label,
            currentMenu = [],
            focusedItem,
            openSidebar,
            closeButtonRef,
            handleClickBackButton,
            handleClickSubMenuIcon,
            handleKeyDownSubMenuIcon,
            handleKeyDownBackButton,
            handleKeyDownMenuItem,
            handleCloseSidebar,
            handleKeyDownCloseButton,
        },
        currentMenuList
    ) => {
        return (
            <StyledUlContainer>
                {openSidebar && handleCloseSidebar && (
                    <CloseButton
                        ref={closeButtonRef}
                        variant="borderless"
                        color="secondary"
                        aria-label={translate("Close menu")}
                        onClick={handleCloseSidebar}
                        onKeyDown={handleKeyDownCloseButton}
                        tabIndex={0}
                    >
                        <Clear24Icon />
                    </CloseButton>
                )}
                <StyledUl
                    ref={currentMenuList}
                    className={"kms-ds-sidebar-menu"}
                    role="menu"
                    aria-label={label}
                    hasBackButton={!!title}
                >
                    {!!title && openSidebar && (
                        <BackButton
                            role={"menuitem"}
                            classes={{ label: "kms-ds-sidebar-menu-back-btn-label" }}
                            variant={"borderless"}
                            onClick={handleClickBackButton}
                            onKeyDown={handleKeyDownBackButton}
                            aria-label={translate("Back to %1", [title])}
                            startIcon={<ChevronLeft24Icon />}
                        >
                            {title}
                        </BackButton>
                    )}
                    {currentMenu.map((menuItem, index: number) => {
                        return (
                            <React.Fragment key={menuItem.id || JSON.stringify(menuItem)}>
                                <SidebarItem
                                    menuItem={{ ...menuItem, active: index === focusedItem }}
                                    handleClickSubMenuIcon={handleClickSubMenuIcon}
                                    handleKeyDownSubMenuIcon={handleKeyDownSubMenuIcon}
                                    index={index}
                                    keyDownItemHandler={handleKeyDownMenuItem}
                                    isSidebarOpen={openSidebar}
                                />
                            </React.Fragment>
                        );
                    })}
                </StyledUl>
            </StyledUlContainer>
        );
    }
);

Menu.displayName = "Menu";

export default Menu;
